import { useEffect } from 'react'
import getPdfMake from 'Logic/pdfmake.js'
import { isAfter } from 'date-fns'
import {
  timeToMinutes,
  patientName,
  formatTimegridAppointmentTime,
  getTreatmentColor,
} from 'Data/format.js'
import * as htmlToImage from 'html-to-image'
import { formatDate } from 'Simple/Data/format.js'
import { useDesignTokenValue } from 'Simple/DesignTokens'

/**
 * NOTES ON THIS PDF LIBRARY:
 *
 * This library has some strange behaviors:
 * - Apparently, when you try to add text to a container, and set font size to something that will make the text larger than the container,
 * it will just not render any of the text and then collapse the container to 0 height.
 * - When you add a table, you need to specify the widts of all columns, otherwise it will error out. If you define only two widths in the array,
 * but you add 3 columns in the array definition [[],[],[]] it will error out.
 * - It supports tables with square cells, but not rounded. You have to use canvas to draw rounded rectangles, and fit the text inside.
 */

let GREEN_CHECK = `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_8777_5400)"><path fill-rule="evenodd" clip-rule="evenodd" d="M7 14C10.866 14 14 10.866 14 7C14 3.13401 10.866 0 7 0C3.13401 0 0 3.13401 0 7C0 10.866 3.13401 14 7 14ZM10.5028 5.21852C10.7615 4.89507 10.7091 4.42311 10.3856 4.16435C10.0622 3.90559 9.59022 3.95803 9.33146 4.28148L5.78557 8.71384L4.36711 7.65C4.03574 7.40147 3.56564 7.46863 3.31711 7.8C3.06859 8.13137 3.13574 8.60147 3.46711 8.85L5.46711 10.35C5.79119 10.5931 6.24971 10.5348 6.50277 10.2185L10.5028 5.21852Z" fill="#3F9142"/></g><defs><clipPath id="clip0_8777_5400"><rect width="14" height="14" fill="white"/></clipPath></defs></svg>`

/** @type {(fillColor: string) => string} */
function getDollarIcon(fillColor) {
  return `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M14 7C14 10.866 10.866 14 7 14C3.13401 14 0 10.866 0 7C0 3.13401 3.13401 0 7 0C10.866 0 14 3.13401 14 7ZM7.74997 3.25C7.74997 2.83579 7.41418 2.5 6.99997 2.5C6.58576 2.5 6.24997 2.83579 6.24997 3.25V3.7605C5.31317 3.85991 4.58334 4.65262 4.58334 5.61578C4.58334 6.49259 5.19386 7.2511 6.05042 7.43847L7.52351 7.76071C7.75279 7.81086 7.91666 8.01429 7.91666 8.24952C7.91666 8.52601 7.69244 8.74997 7.41666 8.74997H6.58334C6.36681 8.74997 6.1806 8.61191 6.1116 8.41671C5.97357 8.02617 5.54507 7.82148 5.15454 7.95951C4.764 8.09755 4.55931 8.52604 4.69734 8.91657C4.93581 9.59128 5.52648 10.101 6.24997 10.2223V10.75C6.24997 11.1642 6.58576 11.5 6.99997 11.5C7.41418 11.5 7.74997 11.1642 7.74997 10.75V10.2223C8.69613 10.0635 9.41666 9.24023 9.41666 8.24952C9.41666 7.30991 8.76248 6.49626 7.84406 6.29536L6.37096 5.97312C6.20304 5.93638 6.08334 5.78768 6.08334 5.61578C6.08334 5.41377 6.24711 5.25 6.44913 5.25H7.41666C7.56467 5.25 7.69689 5.31336 7.78952 5.41685C7.83302 5.46545 7.86673 5.52195 7.8884 5.58326C8.02643 5.9738 8.45493 6.17849 8.84546 6.04046C9.236 5.90242 9.44069 5.47393 9.30266 5.08339C9.21458 4.83419 9.07893 4.60833 8.90722 4.41648C8.61206 4.0867 8.20721 3.85458 7.74997 3.77772V3.25Z" fill="${fillColor}"/></svg>`
}
let AVG_REGULAR_CHARACTER_WIDTH = 0.55
let AVG_BOLD_CHARACTER_WIDTH = 0.65

/** @type Record<string,string> */
let APPOINTMENT_STATES = {
  CHECKED_IN: 'Checked In',
  CHECKED_OUT: 'Checked Out',
  CONFIRMED: 'Confirmed',
  NO_SHOW: 'No Show',
  OFFICE_CANCELLED: 'Office Cancelled',
  PATIENT_CANCELLED: 'Patient Cancelled',
  SCHEDULED: 'Scheduled',
  UNSEATED: 'Unseated',
}

/**
 * @typedef {{id: string, resourceId: string, start: number, end: number, startStr: string, endStr: string, timeStr: string, extendedProps: Record<string, any>, splitEvent?: boolean, isFirstPart?: boolean, isSecondPart?: boolean}} Event
 * @typedef {{start: number, end: number}} Slot
 * @typedef {{id: string, note: string, author: {id: string, person: {id: string, first_name: string, last_name: string}}}} AppointmentNote
 */

let PAGE_WIDTH = 841.89
let PAGE_HEIGHT = 595

/**
 * @param {{calendarRef: React.MutableRefObject<import('@fullcalendar/react').default | null>, hipaa: boolean, fileName: string, viewPath:string}} param
 * @returns
 */
export default function useOnPrint({ calendarRef, hipaa, fileName, viewPath }) {
  let blue = useDesignTokenValue({
    viewPath,
    path: 'colors.blue.500',
  })

  let red = useDesignTokenValue({
    viewPath,
    path: 'colors.red.500',
  })

  let yellow = '#C99A2E'

  async function handleCustomPrintEvent() {
    let api = calendarRef?.current?.getApi()
    if (!api) {
      return
    }

    /**
     * see design-system/PaymentOverdueIndicator/view.blocks
     * @type (overdues:object[]) => string|null
     */
    function getOverdueColor(overdues) {
      if (overdues.length === 0) return null
      let accountTypes = overdues.map(
        item => item.payment_account?.account_type
      )
      if (accountTypes.includes('InsurancePaymentAccount')) {
        if (accountTypes.includes('PersonPaymentAccount')) {
          return red
        }
        return yellow
      }
      return blue
    }

    let pdfMake = await getPdfMake()

    let pdf = await generateCalendarPDF(api, pdfMake, hipaa, getOverdueColor)
    if (pdf) {
      pdf.download(fileName)
    }
  }

  useEffect(() => {
    window.addEventListener('customBeforePrint', handleCustomPrintEvent)

    return () => {
      window.removeEventListener('customBeforePrint', handleCustomPrintEvent)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hipaa])

  return {}
}

/**
 * Format a time value in minutes to display hours, minutes and AM/PM correctly
 * @param {number} timeInMinutes - Time in minutes (e.g., 720 for 12:00)
 * @returns {{hours: string, minutes: string, range: string}} Formatted time components
 */
function formatTimeComponents(timeInMinutes) {
  let hourValue = Math.floor(timeInMinutes / 60)
  let hour12 = hourValue % 12 === 0 ? 12 : hourValue % 12 // Handle 12 correctly
  let minutes = timeInMinutes % 60
  let ampm = hourValue >= 12 ? 'pm' : 'am' // 12pm is PM, not AM

  return {
    hours: hour12.toString(),
    minutes: minutes.toString().padStart(2, '0'),
    range: ampm,
  }
}

/**
 *
 * @param {import('@fullcalendar/core').CalendarApi} calendar
 * @param {import('pdfmake/build/pdfmake')} pdfmake
 * @param {boolean} hipaa
 * @param {function} getOverdueColor
 * @returns
 */
async function generateCalendarPDF(calendar, pdfmake, hipaa, getOverdueColor) {
  // Gather data from FullCalendar
  let resources = calendar.getResources()
  resources.sort((a, b) => a.extendedProps.position - b.extendedProps.position)

  // Calculate column widths
  let margin = 8
  let netWidth = PAGE_WIDTH - margin * 2 - 20
  let netHeight = PAGE_HEIGHT - margin * 2

  // Set time column width based on resource count
  let timeColWidth = resources.length > 10 ? 25 : resources.length > 6 ? 30 : 35
  let resourcesCount = resources.length
  let availableForResources = netWidth - timeColWidth
  let resourceColWidth = Math.floor(availableForResources / resourcesCount)
  let tableWidths = [
    timeColWidth,
    ...Array(resourcesCount).fill(resourceColWidth),
  ]

  // Grab slot config
  let slotMinTimeStr = calendar.getOption('slotMinTime')?.toString()
  let slotMaxTimeStr = calendar.getOption('slotMaxTime')?.toString()

  if (!slotMinTimeStr || !slotMaxTimeStr) {
    return
  }

  let slotMinTime = timeToMinutes(
    slotMinTimeStr.substring(0, slotMinTimeStr.lastIndexOf(':'))
  )
  let slotMaxTime = timeToMinutes(
    slotMaxTimeStr.substring(0, slotMaxTimeStr.lastIndexOf(':'))
  )
  let slotDuration = 5

  let events = calendar
    .getEvents()
    .map(event => {
      return {
        id: event.id,
        start: Math.max(
          timeToMinutes(
            event.startStr.substring(
              event.startStr.indexOf('T') + 1,
              event.startStr.lastIndexOf(':')
            )
          ),
          slotMinTime
        ),
        end: Math.min(
          timeToMinutes(
            event.endStr.substring(
              event.endStr.indexOf('T') + 1,
              event.endStr.lastIndexOf(':')
            )
          ),
          slotMaxTime
        ),
        startTime: event.start,
        endTime: event.end,
        startStr: event.startStr,
        endStr: event.endStr,
        timeStr: formatTimegridAppointmentTime({
          start: event.startStr,
          end: event.endStr,
        }),
        resourceId: event.getResources()[0].id,
        display: event.display,
        extendedProps: event.extendedProps,
      }
    })
    .filter(
      ev =>
        ['appointment', 'note', 'blockTime'].includes(ev.extendedProps.type) &&
        ev.display !== 'background'
    )

  let date = formatDate(events[0]?.startStr, 'iso', 'EEEE, MMMM d, yyyy') || ''
  let location = events[0]?.extendedProps.location_name || ''

  // @ts-ignore
  events = handleOverlappingEvents(events)

  events.sort((a, b) => {
    // If types are the same, sort by startTime
    if (a.startTime === null) return 1
    if (b.startTime === null) return -1
    return isAfter(a.startTime, b.startTime) ? 1 : -1
  })

  // 2) Build an array of time slots (vertical axis)
  /** @type {Array<Slot>} */
  let slots = []
  for (let t = slotMinTime; t < slotMaxTime; t += slotDuration) {
    slots.push({ start: t, end: t + slotDuration })
  }

  events.sort((a, b) => {
    // Prioritize by type
    let typeOrder = { appointment: 1, note: 2, blockTime: 3 }

    // @ts-ignore
    let aTypePriority = typeOrder[a.extendedProps.type] || 4
    // @ts-ignore
    let bTypePriority = typeOrder[b.extendedProps.type] || 4

    return aTypePriority - bTypePriority
  })

  // Calculate total time range in minutes
  let totalMinutes = slotMaxTime - slotMinTime

  /**
   * We paginate the schedule into two pages. However, when office hours exceed 10 hours,
   * splitting purely by time can shrink each event block too much, making text unreadable.
   * Empirically, more than 5 hours per page causes layout issues.
   *
   * Therefore:
   *  - If total office hours <= 10: let the content flow naturally across up to two pages (fit into one page if possible).
   *  - If total office hours > 10: cap at 5 hours per page to maintain readability.
   */
  let pageSegmentHours =
    totalMinutes <= 600
      ? Math.ceil(totalMinutes / (60 * (totalMinutes > 300 ? 2 : 1)))
      : 5
  // Calculate segment size in minutes
  let segmentSizeMinutes = pageSegmentHours * 60

  // Calculate how many segments we need
  let segmentsCount = Math.ceil(totalMinutes / segmentSizeMinutes)

  // Create an array of page boundaries
  let pageBoundaries = [slotMinTime]

  // Generate page boundaries for each segment
  for (let i = 1; i < segmentsCount; i++) {
    let boundaryTime = slotMinTime + i * segmentSizeMinutes

    // Round to nearest half hour for clean page breaks
    let remainder = boundaryTime % 30
    if (remainder < 15) {
      boundaryTime = boundaryTime - remainder
    } else {
      boundaryTime = boundaryTime + (30 - remainder)
    }

    pageBoundaries.push(boundaryTime)
  }

  // Add the end boundary
  pageBoundaries.push(slotMaxTime)

  // Process events to handle cross-page events
  /** @type {Array<Event>} */
  let processedEvents = []

  events.forEach(event => {
    // Check each page boundary to see if the event crosses it
    for (let i = 1; i < pageBoundaries.length - 1; i++) {
      let boundaryTime = pageBoundaries[i]

      // If event crosses this boundary, split it
      if (event.start < boundaryTime && event.end > boundaryTime) {
        // First extract the date part from the original startStr
        let datePart = ''
        if (event.startStr && event.startStr.includes('T')) {
          datePart = event.startStr.substring(0, event.startStr.indexOf('T'))
        }

        // Calculate hours and minutes for boundary
        let boundaryHours = Math.floor(boundaryTime / 60)
        let boundaryMins = boundaryTime % 60

        // Format ISO string for boundary time
        let boundaryISOString = `${datePart}T${boundaryHours
          .toString()
          .padStart(2, '0')}:${boundaryMins.toString().padStart(2, '0')}:00`

        // Check if we already processed this event up to a different boundary
        let existingProcessedEvent = processedEvents.find(
          pe => pe.id === event.id || pe.id.startsWith(`${event.id}-part`)
        )

        if (existingProcessedEvent) {
          // We've already processed this event partly, now split from previous boundary to current
          if (existingProcessedEvent.end === boundaryTime) {
            // Skip if we've already split at this exact boundary
            continue
          }

          // Split the last part and adjust
          let lastPart = processedEvents.find(
            pe =>
              (pe.id === event.id || pe.id.startsWith(`${event.id}-part`)) &&
              !(pe.end < boundaryTime)
          )

          if (lastPart) {
            // Adjust the end of the last part to this boundary
            lastPart.end = boundaryTime
            lastPart.endStr = boundaryISOString
            lastPart.splitEvent = true

            // Create a new part from this boundary to the end
            let newPart = {
              ...event,
              id: `${event.id}-part${i + 1}`,
              start: boundaryTime,
              startStr: boundaryISOString,
              splitEvent: true,
              isSecondPart: true,
            }

            processedEvents.push(newPart)
          }
        } else {
          // Event for first page (before boundary)
          let event1 = {
            ...event,
            id: `${event.id}-part1`,
            end: boundaryTime,
            endStr: boundaryISOString,
            splitEvent: true,
            isFirstPart: true,
          }

          // Event for second page (after boundary)
          let event2 = {
            ...event,
            id: `${event.id}-part2`,
            start: boundaryTime,
            startStr: boundaryISOString,
            splitEvent: true,
            isSecondPart: true,
          }

          // Keep original ISO format if it exists
          if (event.startTime && typeof event.startTime === 'string') {
            event2.startTime = event.startTime
          }

          if (event.endTime && typeof event.endTime === 'string') {
            event1.endTime = event.endTime
          }

          processedEvents.push(event1, event2)
        }
      }
    }

    // If the event wasn't split (doesn't cross any boundary), add it as is
    let wasProcessed = processedEvents.some(
      pe => pe.id === event.id || pe.id.startsWith(`${event.id}-part`)
    )

    if (!wasProcessed) {
      processedEvents.push(event)
    }
  })

  // This will store the content of the PDF
  /** @type {import('pdfmake/interfaces.js').Content} */
  let content = []

  // Generate a page for each segment
  for (let pageIndex = 0; pageIndex < pageBoundaries.length - 1; pageIndex++) {
    // Get the time range for this page
    let pageStartTime = pageBoundaries[pageIndex]
    let pageEndTime = pageBoundaries[pageIndex + 1]

    // Calculate slots for this page
    let startSlotIndex = Math.floor(
      (pageStartTime - slotMinTime) / slotDuration
    )
    let endSlotIndex = Math.ceil((pageEndTime - slotMinTime) / slotDuration)
    let pageSlotsCount = endSlotIndex - startSlotIndex

    // Calculate the number of slots that would be in a full segment of PAGE_SEGMENT_HOURS
    let fullSegmentSlotsCount = Math.ceil(
      (pageSegmentHours * 60) / slotDuration
    )

    // Use consistent slot height across all pages based on a full segment
    let slotHeight =
      (netHeight - 2 * fullSegmentSlotsCount) / fullSegmentSlotsCount

    // Filter events that belong on this page
    let pageEvents = processedEvents.filter(
      event => event.start < pageEndTime && event.end > pageStartTime
    )

    // Create table for this page
    /** @type {import('pdfmake/interfaces.js').TableCell[][]} */
    let tableBody = []

    // Add header row
    /** @type {import('pdfmake/interfaces.js').TableCell[]} */
    let headerRow = [
      { text: '', border: [false, true, true, true] },
      ...resources.map(r => {
        /** @type {import('pdfmake/interfaces.js').TableCell} */
        let cell = {
          margin: [2, 2, 2, 2],
          stack: [
            {
              text: r.extendedProps.name,
              width: resourceColWidth - 2,
              style: 'timeslotCell',
              bold: true,
            },
            {
              text:
                // @ts-ignore
                r.extendedProps.allocations.find(alloc => alloc.is_provider)
                  ?.name ?? 'No provider',
              width: resourceColWidth - 2,
              style: 'eventText',
            },
            {
              text:
                // @ts-ignore
                r.extendedProps.allocations.find(alloc => alloc.is_assistant)
                  ?.name ?? '-',
              width: resourceColWidth - 2,
              style: 'eventText',
              color: '#616e7c',
            },
          ],
        }
        return cell
      }),
    ]
    tableBody.push(headerRow)

    // Create rows for each time slot on this page
    /** @type {import('pdfmake/interfaces.js').TableCell[][]} */
    let rows = Array.from({ length: pageSlotsCount }).map(_ =>
      Array.from({ length: resources.length + 1 }).map(_ => ({ text: '' }))
    )

    // Fill in time slots
    for (let i = startSlotIndex; i < endSlotIndex; i++) {
      let slotIndex = i - startSlotIndex
      let currentSlot = slots[i]

      // Format the time for this slot
      let timeDisplay = formatTimeComponents(currentSlot.start)

      // Add time label to first column
      rows[slotIndex][0] = createSlotLabelCell({
        // Show hour when on the hour or first slot on page
        hours:
          currentSlot.start % 60 === 0 || i === startSlotIndex
            ? timeDisplay.hours
            : undefined,
        // Always show minutes
        minutes: timeDisplay.minutes,
        // Show am/pm when on the hour or first slot on page
        range:
          currentSlot.start % 60 === 0 || i === startSlotIndex
            ? timeDisplay.range
            : undefined,
      })
    }

    // Keep track of occupied cells
    let occupiedCells = new Set()

    // Place events on the page
    for (let event of pageEvents) {
      // Find the resource index
      let resourceIndex = resources.findIndex(r => r.id === event.resourceId)
      if (resourceIndex === -1) return

      // Calculate event position on page
      let eventStartOnPage = Math.max(event.start, pageStartTime)
      let eventEndOnPage = Math.min(event.end, pageEndTime)

      // Convert to slot indices
      let eventStartSlot = Math.floor(
        (eventStartOnPage - pageStartTime) / slotDuration
      )
      let eventEndSlot = Math.ceil(
        (eventEndOnPage - pageStartTime) / slotDuration
      )

      // Calculate row span
      let rowSpan = eventEndSlot - eventStartSlot

      // Check if any slots are already occupied
      let canPlace = true
      for (let i = 0; i < rowSpan; i++) {
        let cellKey = `${eventStartSlot + i}-${resourceIndex}`
        if (occupiedCells.has(cellKey)) {
          canPlace = false
          break
        }
      }

      if (canPlace) {
        // Create the event cell with continuation marking based on split event
        let isContinuation = (event.splitEvent && event.isSecondPart) ?? false
        let willContinue = (event.splitEvent && event.isFirstPart) ?? false

        let cell = await getCell({
          event,
          isContinuation,
          willContinue,
          rowSpan,
          cellWidth: resourceColWidth,
          slotHeight,
          hipaa,
          getOverdueColor,
        })

        // Place the cell
        rows[eventStartSlot][resourceIndex + 1] = cell

        // Mark cells as occupied
        for (let i = 0; i < rowSpan; i++) {
          let cellKey = `${eventStartSlot + i}-${resourceIndex}`
          occupiedCells.add(cellKey)
        }
      }
    }

    tableBody.push(...rows)

    // Add the table to content
    content.push({
      table: {
        heights: function (row) {
          if (row === 0) {
            // Header row - keep it at a reasonable fixed height
            return 24 // Fixed height for resource header cells
          }
          return slotHeight // Height for other rows
        },
        headerRows: 1,
        widths: tableWidths,
        body: tableBody,
      },
      layout: {
        hLineWidth: function (i, node) {
          return 1
        },
        vLineWidth: function (i, node) {
          return 1
        },
        hLineColor: function (i, node) {
          return '#E9ECEF'
        },
        vLineColor: function (i, node) {
          return '#E9ECEF'
        },
        paddingLeft: function (i, node) {
          return 0
        },
        paddingRight: function (i, node) {
          return 0
        },
        paddingTop: function (i, node) {
          return 0
        },
        paddingBottom: function (i, node) {
          return 0
        },
      },
      // Add pageBreak after each page except the last one
      ...(pageIndex < pageBoundaries.length - 2 ? { pageBreak: 'after' } : {}),
    })
  }

  /** @type {import('pdfmake/interfaces.js').TDocumentDefinitions} */
  let docDefinition = {
    pageOrientation: 'landscape',
    pageSize: 'A4',
    header: (currentPage, pageCount) => ({
      text: `${location} - ${date} - ${currentPage === 1 ? 'AM' : 'PM'}`,
      alignment: 'left',
      fontSize: 9,
      bold: true,
      margin,
    }),
    content,
    styles: {
      timeSlotCell: {
        fontSize: 6.5,
        color: '#9aa5b1',
      },
      eventText: {
        fontSize: 5.5,
        color: '#323f4b',
      },
      eventTextSecondary: {
        fontSize: 4.5,
        color: '#323f4b',
      },
    },
    pageMargins: [margin, 20, margin, margin],
    defaultStyle: {
      fontSize: 5.5,
    },
  }

  return pdfmake.createPdf(docDefinition)
}

/**
 * Simulate how text will render in available space, accounting for wrapping
 * @param {string} text - The text to fit
 * @param {number} availableWidth - Available width in PDF units
 * @param {number} maxLines - Maximum number of lines allowed
 * @param {number} fontSize - Font size in points
 * @param {string} fontType - Font type (normal, bold)
 * @returns {{lines: number, text: string[], fits: boolean}} - Resulting text data
 */
function calculateTextFit(
  text,
  availableWidth,
  maxLines,
  fontSize = 5,
  fontType = 'normal'
) {
  if (!text) return { lines: 0, text: [], fits: true }

  // Approximate character width based on font size
  let avgCharWidth =
    fontType === 'bold'
      ? fontSize * AVG_BOLD_CHARACTER_WIDTH
      : fontSize * AVG_REGULAR_CHARACTER_WIDTH

  // Split by words for wrapping simulation
  let words = text.split(' ')
  let lines = []
  let currentLine = ''
  let allContentFits = true

  for (let word of words) {
    let testLine = currentLine ? `${currentLine} ${word}` : word
    let testLineWidth = testLine.length * avgCharWidth

    if (testLineWidth <= availableWidth) {
      currentLine = testLine
    } else {
      if (currentLine) {
        lines.push(currentLine)
      } else {
        // Word is too long for a single line, need to truncate it
        let charsPerLine = Math.floor(availableWidth / avgCharWidth)
        lines.push(word.substring(0, charsPerLine - 3) + '...')
        allContentFits = false
        break
      }

      // Start new line with this word
      currentLine = word

      // Check if we've reached max lines
      if (lines.length >= maxLines - 1) {
        // We can't add any more lines after this one
        if (currentLine) {
          lines.push(
            currentLine + (words.indexOf(word) < words.length - 1 ? '...' : '')
          )
        }

        // There's more content that won't fit
        if (words.indexOf(word) < words.length - 1) {
          allContentFits = false
        }

        break
      }
    }
  }

  // Add the last line if there's room and content
  if (currentLine && lines.length < maxLines) {
    lines.push(currentLine)
  }

  // Check if we've hit our line limit but still have more words
  if (lines.length === maxLines && !allContentFits) {
    // Add ellipsis to the last line if it doesn't already have one
    let lastLine = lines[lines.length - 1]
    if (!lastLine.endsWith('...')) {
      lines[lines.length - 1] =
        lastLine.substring(0, lastLine.length - Math.min(3, lastLine.length)) +
        '...'
    }
  }

  return {
    lines: lines.length,
    text: lines,
    fits: allContentFits,
  }
}

/**
 * @param {{event: Event, isContinuation: boolean, willContinue: boolean, rowSpan: number, cellWidth: number, slotHeight: number, hipaa: boolean, getOverdueColor: function}} params
 * @returns {Promise<import('pdfmake/interfaces.js').TableCell>}
 */
async function getCell({
  event,
  isContinuation,
  willContinue,
  rowSpan,
  cellWidth,
  slotHeight,
  hipaa,
  getOverdueColor,
}) {
  // Define visual indicators for continuation
  let continuationPrefix = isContinuation ? 'CONT: ' : ''
  let continuationSuffix = willContinue ? ' (CONT)' : ''

  // Calculate cell height
  let cellHeight = rowSpan * slotHeight + (rowSpan > 1 ? rowSpan : 1)

  // Calculate available width for text (accounting for padding)
  let textAvailableWidth = cellWidth - 10 // Subtracting left and right padding

  // Calculate max lines that could fit in the available height
  let lineHeight = 6 // Approximate line height in points
  let verticalPadding = 4 // Top and bottom padding
  let maxLines = Math.max(
    1,
    Math.floor((cellHeight - verticalPadding) / lineHeight)
  )

  switch (event.extendedProps.type) {
    case 'appointment': {
      // Prepare text content
      let patientNameText = patientName({
        ...event.extendedProps.appointment.patient.person,
        last_name:
          !hipaa && event.extendedProps.appointment.patient.person.last_name,
      })

      let appointmentType = event.extendedProps.appointment.type.display_name

      // Apply continuation markers
      patientNameText =
        continuationPrefix + patientNameText + continuationSuffix

      // Calculate if all content would fit when properly wrapped
      let patientNameFit = calculateTextFit(
        patientNameText,
        textAvailableWidth,
        maxLines,
        5,
        'bold'
      )
      let linesRemaining = maxLines - patientNameFit.lines

      // Only calculate appointment type fit if we have room for it
      let appointmentTypeFit = null
      if (linesRemaining > 0) {
        appointmentTypeFit = calculateTextFit(
          appointmentType,
          textAvailableWidth,
          linesRemaining
        )
        linesRemaining -= appointmentTypeFit.lines
      }

      // Only calculate time fit if we have room for it
      let timeFit = null
      if (linesRemaining > 0) {
        timeFit = calculateTextFit(
          event.timeStr,
          textAvailableWidth,
          linesRemaining
        )

        linesRemaining -= timeFit.lines
      }

      let appointmentStatus =
        linesRemaining > 0 ? APPOINTMENT_STATES[event.extendedProps.state] : ''

      // Build the stack content based on what fits
      /** @type {import('pdfmake/interfaces.js').Content} */
      let stackContent = []

      /** @type string[] */
      let colors = (
        event.extendedProps.appointment?.tx?.colors?.split(',') ?? []
      ).map(x => getTreatmentColor(x))
      // Patient name is always included (potentially truncated)
      stackContent.push({
        layout: 'noBorders',
        margin: [0, -3, 0, -3],
        table: {
          widths: ['*', 'auto'],
          body: [
            [
              [
                {
                  text: Array.isArray(patientNameFit.text)
                    ? patientNameFit.text.join(' ')
                    : patientNameFit.text,
                  style: 'eventText',
                  margin: [0, 0, 0, 1],
                },
              ],
              [
                {
                  margin: [0, 3, 3, 0],
                  canvas: colors.map((color, i) => ({
                    type: 'ellipse',
                    x: 5 * i,
                    y: 0,
                    r1: 2,
                    color,
                  })),
                },
              ],
            ],
          ],
        },
      })

      // Add appointment type if it fits
      if (appointmentTypeFit && appointmentTypeFit.lines > 0) {
        stackContent.push({
          text: Array.isArray(appointmentTypeFit.text)
            ? appointmentTypeFit.text.join(' ')
            : appointmentTypeFit.text,
          style: 'eventText',
          margin: [0, 0, 0, 1],
        })

        let overdueColor = getOverdueColor(
          event.extendedProps.appointment.patient.due_amount
        )

        // Add time if it also fits
        if (timeFit && timeFit.lines > 0) {
          let isConfirmed =
            event.extendedProps.confirmation_status === 'CONFIRMED'

          stackContent.push({
            layout: 'noBorders',
            margin: [0, -3, 0, 0],
            table: {
              widths: ['auto', 'auto', 'auto'],
              body: [
                [
                  {
                    text: Array.isArray(timeFit.text)
                      ? timeFit.text.join(' ')
                      : timeFit.text,
                    style: 'eventText',
                    margin: [0, 0, 0, 0],
                  },
                  [
                    isConfirmed
                      ? {
                          svg: GREEN_CHECK,
                          width: 5.5,
                          height: 5.5,
                          margin: [-5, 0, 0, 0],
                        }
                      : null,
                  ],
                  [
                    overdueColor
                      ? {
                          svg: getDollarIcon(overdueColor),
                          width: 5.5,
                          height: 5.5,
                          margin: [-5, 0, 0, 0],
                        }
                      : null,
                  ],
                ],
              ],
            },
          })
        }
      }

      if (appointmentStatus) {
        let appointmentStatusFontSize = 4
        stackContent.push({
          margin: [0, -2, 0, 0],
          stack: [
            {
              canvas: [
                {
                  type: 'rect',
                  x: 0,
                  y: 0,
                  w:
                    1 +
                    appointmentStatusFontSize *
                      appointmentStatus.length *
                      AVG_REGULAR_CHARACTER_WIDTH,
                  h: 5,
                  r: 1,
                  color: '#ffffff',
                  lineColor: '#cacaca',
                  lineWidth: 0.2,
                },
              ],
            },
            {
              text: appointmentStatus,
              fontSize: appointmentStatusFontSize,
              margin: [1, -5, 0, 0],
              color: '#232323',
            },
          ],
        })
      }

      /** @type {{note:string}[]} */
      let notes = event.extendedProps.appointment.notes

      if (notes.length > 0 && linesRemaining > 0) {
        let maxWidth = textAvailableWidth
        let maxHeight = linesRemaining * lineHeight + 2
        let fontSize = 5

        let wrapper = document.createElement('div')
        wrapper.style.maxWidth = `${maxWidth}px`
        wrapper.style.maxHeight = `${maxHeight}px`
        wrapper.style.overflow = 'hidden'
        wrapper.style.display = 'inline-block'
        wrapper.style.backgroundColor = '#fff'
        wrapper.style.padding = '0'
        wrapper.style.margin = '0'

        document.body.appendChild(wrapper)

        for (let i = 0; i < notes.length; i++) {
          let note = notes[i]

          let node = document.createElement('div')
          node.innerHTML = note.note

          node.style.display = 'block'
          node.style.fontFamily = 'Arial, sans-serif'
          node.style.fontSize = `${fontSize}px`
          node.style.lineHeight = '1'
          node.style.margin = '0'
          node.style.padding = '0'
          node.style.background = '#fff'
          node.style.wordBreak = 'break-all'
          node.style.maxWidth = `${maxWidth}px`
          node.style.marginTop = i > 0 ? '-4px' : '-2px'

          wrapper.appendChild(node)
        }

        let png = await htmlToImage.toPng(wrapper, {
          backgroundColor: undefined,
          width: maxWidth,
          height: maxHeight,
          pixelRatio: 8,
        })

        stackContent.push({
          image: png,
          width: maxWidth,
          height: maxHeight,
        })

        document.body.removeChild(wrapper)
      }

      // Create a simplified outer structure with manual border settings
      return {
        rowSpan: rowSpan,
        margin: [0, 0, 0, 0], // No margin
        stack: [
          {
            // Use a canvas for the entire card background including color strip
            canvas: [
              // Main card background
              {
                type: 'rect',
                x: 2, // Shifted right to leave space for color strip
                y: 0,
                w: cellWidth - 4,
                h: cellHeight,
                r: 3,
                color: '#ffffff',
                lineColor: '#e0e0e0',
                lineWidth: 0.5,
              },
              // Left color strip
              {
                type: 'rect',
                x: 0,
                y: 0,
                w: 3,
                h: cellHeight,
                color: event.extendedProps.appointment_type_color,
                lineWidth: 0,
              },
            ],
          },
          // Text content
          {
            // Position text content with fixed positioning
            margin: [6, -cellHeight + 2, 2, 0],
            // Text stack with proper spacing
            stack: stackContent,
          },
        ],
      }
    }

    case 'note': {
      // Get note content
      let noteContent = event.extendedProps.content || ''

      // Apply continuation markers
      noteContent = continuationPrefix + noteContent + continuationSuffix

      // Calculate text fitting with wrapping
      let noteFit = calculateTextFit(noteContent, textAvailableWidth, maxLines)

      // Create a card-like appearance for notes
      return {
        rowSpan: rowSpan,
        margin: [0, 0, 0, 0], // No margin
        stack: [
          {
            // Use a canvas for the card background
            canvas: [
              // Note card background
              {
                type: 'rect',
                x: 0,
                y: 0,
                w: cellWidth - 2,
                h: cellHeight,
                r: 3,
                color: '#F8E3A3',
                lineColor: '#e9d28c',
                lineWidth: 0.5,
              },
            ],
          },
          // Note text
          {
            // Position text with fixed positioning
            margin: [4, -cellHeight + 2, 4, 0],
            stack: noteFit.text.slice(0, maxLines).map(line => ({
              text: line,
              style: 'eventText',
            })),
          },
        ],
      }
    }

    case 'blockTime': {
      // Get block time content
      let blockContent = event.extendedProps.content || ''

      // Apply continuation markers
      blockContent = continuationPrefix + blockContent + continuationSuffix

      // Calculate text fitting with wrapping
      let blockFit = calculateTextFit(
        blockContent,
        textAvailableWidth,
        maxLines
      )

      // Create a card-like appearance for block time
      return {
        rowSpan: rowSpan,
        margin: [0, 0, 0, 0], // No margin
        stack: [
          {
            // Use a canvas for the card background
            canvas: [
              // Block time card background
              {
                type: 'rect',
                x: 0,
                y: 0,
                w: cellWidth - 2,
                h: cellHeight,
                r: 3,
                color: '#CBD2D9',
                lineColor: '#b0b7bd',
                lineWidth: 0.5,
              },
            ],
          },
          // Block time text with multi-line support
          {
            // Position text with fixed positioning
            margin: [4, -cellHeight + 3, 4, 0],
            stack: blockFit.text.slice(0, maxLines).map(line => ({
              text: line,
              style: 'eventText',
            })),
          },
        ],
      }
    }

    default:
      return { text: '' }
  }
}

/**
 * @param {{hours?: string, range?: string, minutes: string}} params
 * @returns {import('pdfmake/interfaces').TableCell}
 */
function createSlotLabelCell({ hours, range, minutes }) {
  return {
    columns: [
      ...(hours
        ? [
            {
              text: hours,
              style: 'timeSlotCell',
              color: '#9aa5b1',
              bold: true,
              margin: [-3, 0, 1, 0],
            },
          ]
        : []),

      ...(range
        ? [
            {
              text: range,
              style: 'eventText',
              color: '#9aa5b1',
              bold: true,
              margin: [-1, 0, 1, 0],
            },
          ]
        : []),

      {
        text: minutes,
        style: 'eventText',
        color: '#7b8794',
        margin: [0, 0, 0, 0],
      },
    ],
    alignment: 'right',
    margin: [0, 0, 0, 0],
    border: [false, false, true, false],
  }
}

/**
 * Process events to handle overlapping appointments and block times
 * This function splits block times into segments when they overlap with appointments
 *
 * @param {Array<Event>} events - The original event array
 * @returns {Array<Event>} - The processed events with split blocks
 */
function handleOverlappingEvents(events) {
  // Make a deep copy of the events to avoid modifying the original array
  let eventsCopy = events.map(event => ({ ...event }))

  // Sort events by priority (appointment > note > blockTime)
  let typeOrder = { appointment: 1, note: 2, blockTime: 3 }
  eventsCopy.sort((a, b) => {
    // @ts-ignore
    let aTypePriority = typeOrder[a.extendedProps.type] || 4
    // @ts-ignore
    let bTypePriority = typeOrder[b.extendedProps.type] || 4
    return aTypePriority - bTypePriority
  })

  // Separate high-priority events (appointments and notes) from block times
  let highPriorityEvents = eventsCopy.filter(
    e =>
      e.extendedProps.type === 'appointment' || e.extendedProps.type === 'note'
  )
  let blockEvents = eventsCopy.filter(e => e.extendedProps.type === 'blockTime')

  // Start with all high-priority events
  let processedEvents = [...highPriorityEvents]

  // Process each block time
  blockEvents.forEach(blockEvent => {
    // Find all high priority events that overlap this block
    let overlappingEvents = highPriorityEvents.filter(
      e =>
        e.resourceId === blockEvent.resourceId &&
        e.start < blockEvent.end &&
        e.end > blockEvent.start
    )

    if (overlappingEvents.length === 0) {
      // No overlaps, add as-is
      processedEvents.push(blockEvent)
      return
    }

    // Sort overlapping events by start time
    overlappingEvents.sort((a, b) => a.start - b.start)

    // Generate segments
    let segments = []
    let currentStart = blockEvent.start

    for (let event of overlappingEvents) {
      // Add segment before this event if needed
      if (currentStart < event.start) {
        segments.push({
          start: currentStart,
          end: event.start,
          boundaryTime: event.start,
        })
      }

      // Move current position to after this event
      currentStart = Math.max(currentStart, event.end)
    }

    // Add final segment if needed
    if (currentStart < blockEvent.end) {
      segments.push({
        start: currentStart,
        end: blockEvent.end,
        boundaryTime: currentStart, // For the startStr
      })
    }

    // Create block time segments with proper ISO strings
    segments.forEach((segment, index) => {
      // Extract date part from the original startStr (same as your page-splitting logic)
      let datePart = ''
      if (blockEvent.startStr && blockEvent.startStr.includes('T')) {
        datePart = blockEvent.startStr.substring(
          0,
          blockEvent.startStr.indexOf('T')
        )
      }

      // For each segment boundary, create ISO strings
      /** @type {(timeInMinutes: number) => string} */
      let createISOString = timeInMinutes => {
        let { hours, minutes, range } = formatTimeComponents(timeInMinutes)
        hours += range === 'pm' ? 12 : 0
        return `${datePart}T${hours.toString().padStart(2, '0')}:${minutes}:00`
      }

      // Create ISO strings for segment boundaries
      let segmentStartISOString, segmentEndISOString

      if (index === 0) {
        // First segment: start from original, end at boundary
        segmentStartISOString = blockEvent.startStr
        segmentEndISOString = createISOString(segment.end)
      } else {
        // Other segments: both boundaries are calculated
        segmentStartISOString = createISOString(segment.start)
        segmentEndISOString =
          index === segments.length - 1
            ? blockEvent.endStr
            : createISOString(segment.end)
      }

      // Clone the original block event with new segment data
      let splitBlock = {
        ...blockEvent,
        id: `${blockEvent.id}-part${index + 1}`,
        start: segment.start,
        end: segment.end,
        startStr: segmentStartISOString,
        endStr: segmentEndISOString,
        splitEvent: segments.length > 1,
        isFirstPart: index === 0 && segments.length > 1,
        isSecondPart: index > 0 && segments.length > 1,
      }

      processedEvents.push(splitBlock)
    })
  })

  return processedEvents
}
